// Members-facing Org Fleet view. Shows the same pledged ships as the leadership view
// but with NO owner identity — anyone in the org can see what the org has, the
// "who" stays leadership-only. See docs/org_fleet_v1_plan.md.

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Search, Package, Flag, Clock, Users } from 'lucide-react';
import api from '../api/axios';
import styles from '../styles/UserFleet.module.css';
import tficLogo from '../assets/Icons/NEWTFICLOGO_no_bg.png';

interface PublicEntry {
  shipId?: number;
  shipName: string;
  manufacturer: string;
  role: string;
  imageUrl?: string;
  cargoCapacity?: number;
  crew?: number;
  count: number;
  mostRecentPledgedAt?: string;
}

interface NamedCount {
  name: string;
  count: number;
}

interface PublicStats {
  totalShips: number;
  totalCargo: number;
  totalCrew: number;
  byManufacturer: NamedCount[];
  byRole: NamedCount[];
}

const OrgFleetMembers: React.FC = () => {
  const [entries, setEntries] = useState<PublicEntry[]>([]);
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [fleetRes, statsRes] = await Promise.all([
        api.get('/org/fleet/public'),
        api.get('/org/fleet/public/stats'),
      ]);
      setEntries(Array.isArray(fleetRes.data) ? fleetRes.data : []);
      setStats(statsRes.data || null);
    } catch (err: any) {
      console.error('Error loading org fleet:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to load org fleet');
    } finally {
      setLoading(false);
    }
  };

  const manufacturers = useMemo(
    () => Array.from(new Set(entries.map(e => e.manufacturer))).filter(Boolean).sort(),
    [entries]
  );
  const roles = useMemo(
    () => Array.from(new Set(entries.map(e => e.role))).filter(Boolean).sort(),
    [entries]
  );

  const filtered = useMemo(() => {
    let result = [...entries];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.shipName.toLowerCase().includes(q) ||
        e.manufacturer.toLowerCase().includes(q));
    }
    if (filterManufacturer !== 'all') result = result.filter(e => e.manufacturer === filterManufacturer);
    if (filterRole !== 'all') result = result.filter(e => e.role === filterRole);
    return result;
  }, [entries, searchTerm, filterManufacturer, filterRole]);

  const formatPledgedAt = (iso?: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.loading}><p>Loading org fleet...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.emptyState}>
          <Flag size={64} />
          <h3>Cannot load org fleet</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fleetContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Flag className={styles.titleIcon} /> Org Fleet
          </h1>
          <p className={styles.subtitle}>
            Ships pledged by members for TFIC operations. Want to contribute one of yours? Visit your <Link to="/my-fleet">My Fleet</Link> page and toggle "Pledge to Org" on any ship you own.
          </p>
        </div>
      </div>

      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Rocket className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalShips}</div>
                <div className={styles.statLabel}>Pledged Ships</div>
              </div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Package className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalCargo.toLocaleString()} SCU</div>
                <div className={styles.statLabel}>Total Cargo</div>
              </div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Users className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalCrew}</div>
                <div className={styles.statLabel}>Crew Slots</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search ships or manufacturers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={filterManufacturer} onChange={e => setFilterManufacturer(e.target.value)}>
            <option value="all">All Manufacturers</option>
            {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <Flag size={64} />
          <h3>{entries.length === 0 ? 'No ships pledged yet' : 'No ships match these filters'}</h3>
          <p>
            {entries.length === 0
              ? 'Members can pledge ships from their My Fleet page. Be the first!'
              : 'Try clearing one of the filters above.'}
          </p>
        </div>
      ) : (
        <div className={styles.shipGrid}>
          {filtered.map(e => {
            const key = `${e.shipId ?? 'noid'}-${e.shipName}-${e.manufacturer}`;
            return (
              <div key={key} className={`${styles.shipCard} ${styles.shipCardPledged}`}>
                <div className={styles.shipImageContainer}>
                  {e.imageUrl ? (
                    <img src={e.imageUrl} alt={e.shipName} className={styles.shipImage} />
                  ) : (
                    <div className={styles.shipImagePlaceholder} title="No image available for this ship">
                      <img src={tficLogo} alt="" className={styles.placeholderLogo} />
                      <span className={styles.placeholderLabel}>No image</span>
                    </div>
                  )}
                  <div className={styles.pledgeRibbon} title="Pledged to org fleet">
                    <Flag size={12} /> ORG
                  </div>
                  {e.count > 1 && (
                    <div className={styles.countBadge} title={`${e.count} members have pledged this ship`}>
                      ×{e.count}
                    </div>
                  )}
                </div>
                <div className={styles.shipBody}>
                  <h3 className={styles.shipName}>
                    {e.shipName}
                    {e.count > 1 && <span className={styles.shipNameCount}> ×{e.count}</span>}
                  </h3>
                  <p className={styles.shipManufacturer}>{e.manufacturer}</p>
                  <div className={styles.shipDetails}>
                    <div className={styles.shipMeta}>
                      {e.role && <span><strong>Role:</strong> {e.role}</span>}
                      {e.cargoCapacity != null && <span><strong>Cargo:</strong> {e.cargoCapacity} SCU</span>}
                      {e.crew != null && <span><strong>Crew:</strong> {e.crew}</span>}
                      {e.mostRecentPledgedAt && (
                        <span><Clock size={12} /> {e.count > 1 ? 'Last pledged' : 'Pledged'} {formatPledgedAt(e.mostRecentPledgedAt)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrgFleetMembers;
